import * as React from "react";
import { useRecordContext } from "react-admin";
import { Box, Typography, Chip } from "@mui/material";
import { fmtDate as fmtDateFr, fmtDateTime as fmtDateTimeFr, fmtMoney as fmtMoneyFr, isTechnicalKey } from "../utils/format";
import { TechInfo } from "./ui";

// InfoGrid — affiche un ensemble de paires label / valeur dans une grille
// 2 colonnes. Accepte un tableau de { label, source(string|fn), type? }.
// Source : string "a.b.c" pour pick imbriqué, ou fonction (record) => value.
// Type : 'date' (epoch ms), 'datetime', 'bool', 'money', 'chip', 'list', 'mono'.
const pick = (row, path) => {
  if (typeof path === "function") return path(row);
  if (!path) return undefined;
  const parts = path.split(".");
  let v = row;
  for (const p of parts) v = v == null ? undefined : v[p];
  return v;
};

const fmtDate = (v, withTime = false) => {
  const out = withTime ? fmtDateTimeFr(v, null) : fmtDateFr(v, null);
  return out;
};

const fmtMoney = (v) => fmtMoneyFr(v, null);

const renderValue = (raw, type) => {
  if (raw == null || raw === "") return <Typography variant="body2" color="text.disabled">—</Typography>;
  if (type === "date") return <Typography variant="body2">{fmtDate(raw) || "—"}</Typography>;
  if (type === "datetime") return <Typography variant="body2">{fmtDate(raw, true) || "—"}</Typography>;
  if (type === "bool") return (
    <Chip size="small" label={raw ? "Oui" : "Non"} color={raw ? "success" : "default"} variant="outlined" />
  );
  if (type === "money") return <Typography variant="body2">{fmtMoney(raw) || "—"}</Typography>;
  if (type === "chip") return <Chip size="small" label={String(raw)} />;
  if (type === "list" && Array.isArray(raw)) return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {raw.length === 0
        ? <Typography variant="body2" color="text.disabled">—</Typography>
        : raw.map((x, i) => <Chip key={i} size="small" label={typeof x === "object" ? JSON.stringify(x) : String(x)} />)}
    </Box>
  );
  if (type === "mono") return (
    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
      {String(raw)}
    </Typography>
  );
  if (typeof raw === "object") return (
    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
      {JSON.stringify(raw)}
    </Typography>
  );
  return <Typography variant="body2">{String(raw)}</Typography>;
};

// Les identifiants techniques (source « id », « xxxId », type « mono » dont le
// libellé mentionne un ID…) ne sont plus affichés dans la grille : ils sont
// regroupés dans un bloc replié « Identifiants techniques » (avec copie).
const isTechField = (f) =>
  (typeof f.source === "string" && isTechnicalKey(f.source)) ||
  (f.type === "mono" && /(^|\W)(ID|UID|Id)(\W|$)|Account ID|Idrainbow/.test(f.label || ""));

export const InfoGrid = ({ fields, cols = 2, showTech = true }) => {
  const record = useRecordContext();
  if (!record) return null;
  const visible = fields.filter((f) => !isTechField(f));
  const tech = fields
    .filter(isTechField)
    .map((f) => ({ key: f.label, label: f.label, value: pick(record, f.source) }))
    .filter((x) => x.value != null && x.value !== "");
  return (
    <Box sx={{ p: 0.5 }}>
      <Box
        sx={{
          display: "grid",
          columnGap: { xs: 2, sm: 3 },
          rowGap: 2,
          gridTemplateColumns: cols === 1 ? "1fr" : { xs: "repeat(auto-fill, minmax(120px, 1fr))", sm: "repeat(auto-fill, minmax(150px, 1fr))" },
        }}
      >
        {visible.map((f, i) => (
          <Box key={f.label + i} sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}>
              {f.label}
            </Typography>
            <Box sx={{ mt: 0.25, wordBreak: "break-word" }}>{renderValue(pick(record, f.source), f.type)}</Box>
          </Box>
        ))}
      </Box>
      {showTech && tech.length > 0 && (
        <TechInfo items={tech} showRaw={false} title="Identifiants techniques" sx={{ mt: 2 }} />
      )}
    </Box>
  );
};

export default InfoGrid;
